'use client'

// The centred card a pop-up sits in: the drawer's dim (Drawer.jsx), the cream panel with the ink outline and the stitch
// line inside it (kawaiiPanel), and the round close button the journal's header has (KawaiiButton.jsx), in the middle of
// the screen over the world rather than up from the bottom of it. His bag was the first (Sam, 3 Nov: "I think, the
// drawer should be a popup instead").
//
// Structure only, as Drawer.jsx is: what goes inside is each pop-up's own. Escape closes it, so does a tap on the dim
// world round it and the close button; what closing means (a guard against the tap that opened it, say) is the
// caller's. On a phone it fills most of the screen and its body scrolls inside, never the page sideways.

import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { kawaii } from '../../lib/ui/theme'
import { CloseButton, kawaiiPanel } from './KawaiiButton'

export function Popup({ onClose, title, subtitle = null, width = 760, children }) {
  // One frame closed, then open, so the transition runs.
  const [entered, setEntered] = useState(false)
  useEffect(() => {
    const t = requestAnimationFrame(() => setEntered(true))
    return () => cancelAnimationFrame(t)
  }, [])

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  // Through a portal and at 55, for the drawer's reasons: nothing it opens inside can be placed against it, and the
  // HUD's clock and menu (z-50) stay under it.
  const frame = (
    <div
      className="fixed inset-0 pointer-events-auto flex items-center justify-center"
      style={{ zIndex: 55, padding: 'max(8px, env(safe-area-inset-top)) 8px max(8px, env(safe-area-inset-bottom))' }}
    >
      <div
        className="absolute inset-0"
        style={{ background: 'rgba(26,16,10,0.28)', opacity: entered ? 1 : 0, transition: 'opacity 220ms ease' }}
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="relative flex flex-col overflow-hidden"
        style={{
          ...kawaiiPanel(),
          borderRadius: 22,
          width: `min(${width}px, 100%)`,
          maxHeight: 'min(100%, 820px)',
          transform: entered ? 'none' : 'translateY(10px) scale(0.97)',
          opacity: entered ? 1 : 0,
          transition: 'transform 260ms cubic-bezier(0.22, 1, 0.36, 1), opacity 200ms ease',
        }}
      >
        <div style={{ position: 'absolute', inset: 4, border: kawaii.stitch, borderRadius: 18, pointerEvents: 'none', zIndex: 2 }} />
        <div className="shrink-0 flex items-start justify-between gap-3 px-4 sm:px-6 pt-4 pb-3" style={{ borderBottom: kawaii.stitch }}>
          <div className="min-w-0">
            <span className="block" style={{ color: kawaii.ink, fontSize: 19, fontWeight: 800 }}>{title}</span>
            {subtitle && (
              <span className="block mt-1" style={{ color: kawaii.inkSoft, fontSize: 13.5, fontWeight: 600 }}>{subtitle}</span>
            )}
          </div>
          <CloseButton onClick={onClose} />
        </div>
        <div className="kawaii-scroll flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 sm:px-6 pt-4 pb-5">{children}</div>
      </div>
    </div>
  )

  if (typeof document === 'undefined') return frame
  return createPortal(frame, document.body)
}

export default Popup
